import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Utils from "../../utils";
import { invitationActions } from "../../actions/invitation.actions";
import EnhancedGrid from "../common/EnhancedGrid";
import Title from "../common/Title";
import { MdGppGood, MdInsertInvitation, MdRefresh } from "react-icons/md";
import { FaCopy, FaEnvelopeOpenText } from "react-icons/fa";
import { Toast, ToastContainer, Button, Spinner } from "react-bootstrap";
import { isExpired, decodeToken } from "react-jwt";
import GridHelper from "../helpers/gridHelper";

function InvitationsGrid(props) {
  const location = useLocation();
  const dispatch = useDispatch();
  const invitationReducer = useSelector((state) => state.invitation);
  const [displayToast, setDisplayToast] = useState(false);
  const [lastInvitationUrl, setLastInvitationUrl] = useState("");
  const [displayResendToast, setDisplayResendToast] = useState(false);
  const [resendToastMessage, setResendToastMessage] = useState("");
  const [resendToastVariant, setResendToastVariant] = useState("success");
  const [selectedInvitation, setSelectedInvitation] = useState(null);

  const handleResendInvitation = () => {
    if (selectedInvitation) {
      dispatch(invitationActions.resend(selectedInvitation.invitationId));
    }
  };

  const handleCopyInvitationUrl = () => {
    if (selectedInvitation) {
      const siteUrl = window.location.origin;
      const invitationUrl = `${siteUrl}/register?t=${selectedInvitation.invitationId}`;
      setLastInvitationUrl(invitationUrl);
      navigator.clipboard.writeText(invitationUrl).then(() => {
        setDisplayToast(true);
      });
    }
  };

  const handleRowClick = (invitation) => {
    if (selectedInvitation?.invitationId === invitation.invitationId) {
      setSelectedInvitation(null);
    } else {
      setSelectedInvitation(invitation);
    }
  };

  useEffect(() => {
    let queryParams = Utils.getQueryStringParams(location);
    queryParams["senderUserId"] = props.user.userId;
    queryParams["clientId"] = props.user.clientId;
    queryParams["clientGuid"] = props.user.clientGuid;

    dispatch(invitationActions.getBySender(queryParams));
    // eslint-disable-next-line
  }, [location]);

  // Handle resend success/failure
  useEffect(() => {
    if (invitationReducer.resendSuccessId) {
      setResendToastMessage("Invitation resent successfully! A new email has been sent with a new 7-day token.");
      setResendToastVariant("success");
      setDisplayResendToast(true);
      setSelectedInvitation(null);

      // Refresh the grid to show updated token status
      let queryParams = Utils.getQueryStringParams(location);
      queryParams["senderUserId"] = props.user.userId;
      queryParams["clientId"] = props.user.clientId;
      queryParams["clientGuid"] = props.user.clientGuid;
      dispatch(invitationActions.getBySender(queryParams));
    }
    // eslint-disable-next-line
  }, [invitationReducer.resendSuccessId]);

  useEffect(() => {
    if (invitationReducer.resendError) {
      setResendToastMessage(
        typeof invitationReducer.resendError === "string"
          ? invitationReducer.resendError
          : "Failed to resend invitation. Please try again."
      );
      setResendToastVariant("danger");
      setDisplayResendToast(true);
    }
    // eslint-disable-next-line
  }, [invitationReducer.resendError]);

  let displayColumns = ["email", "createdOn", "expiresOn"];

  const FormattedExpirationDate = ({ token }) => {
    const tokenExpired = isExpired(token);
    const decoded = decodeToken(token);

    if (!decoded || !decoded.exp) {
      return <span className="text-muted">Unknown</span>;
    }

    const expirationDate = new Date(decoded.exp * 1000);
    const formattedDate = expirationDate.toLocaleString();
    const statusClass = tokenExpired ? "text-danger" : "text-success";
    const statusText = tokenExpired ? "(Expired)" : "(Active)";

    return (
      <>
        {formattedDate} <small className={statusClass}>{statusText}</small>
      </>
    );
  };

  const data = invitationReducer.paginatedResult?.data.map((i) => {
    return {
      ...i,
      createdOn: Utils.convertToDateTime(i.createdOn),
      expiresOn: <FormattedExpirationDate token={i.token} />,
    };
  });

  const loading = invitationReducer.loadingGrid;

  return (
    <>
      {!props.hideTitle && (
        <Title
          text="Invitations"
          icon={<MdInsertInvitation className="mb-1" />}
        />
      )}

      {/* Action Buttons Bar */}
      <div className="d-flex gap-2 mb-3 p-3 bg-light rounded"
           style={{
             opacity: selectedInvitation ? 1 : 0.6,
             transition: 'opacity 0.3s ease'
           }}>
        <Button
          variant="outline-primary"
          size="sm"
          disabled={!selectedInvitation}
          onClick={handleCopyInvitationUrl}
          title={!selectedInvitation ? "Select an invitation first" : "Copy the invitation URL to clipboard"}
        >
          <FaCopy className="mb-1 me-2" />
          Copy Invitation URL
        </Button>

        <Button
          variant="outline-primary"
          size="sm"
          disabled={!selectedInvitation || invitationReducer.resending}
          onClick={handleResendInvitation}
          title={!selectedInvitation ? "Select an invitation first" : "Resend the invitation email with a new 7-day token"}
        >
          {invitationReducer.resending && invitationReducer.resendingId === selectedInvitation?.invitationId ? (
            <>
              <Spinner size="sm" className="me-2" />
              Resending...
            </>
          ) : (
            <>
              <MdRefresh className="mb-1 me-2" />
              Resend Invitation
            </>
          )}
        </Button>

        {selectedInvitation && (
          <div className="ms-auto align-self-center">
            <small className="text-muted">
              Selected: <strong>{selectedInvitation.email}</strong>
            </small>
          </div>
        )}
      </div>

      <EnhancedGrid
        gridId="invitation"
        fillHeight={props.hideTitle}
        data={data}
        displayColumns={displayColumns}
        emptyState={{
          icon: <FaEnvelopeOpenText size={80} className="text-muted mb-3" />,
          message: "No invitations found",
          subtitle: "Pending invitations will appear here.",
        }}
        idColumn={"invitationId"}
        pageNumber={(invitationReducer.paginatedResult?.pageNumber ?? 1) - 1}
        totalPages={invitationReducer.paginatedResult?.totalPages ?? 0}
        loading={loading}
        onPageChange={GridHelper.handlePageClick}
        onSort={GridHelper.handleSortClick}
        selectedRow={selectedInvitation}
        onRowClick={handleRowClick}
      />

      <ToastContainer
        className="p-3"
        position={"bottom-end"}
        style={{ zIndex: 1 }}
      >
        <Toast
          onClose={() => setDisplayToast(false)}
          show={displayToast}
          delay={5000}
          autohide
        >
          <Toast.Header>
            <strong className="me-auto">
              <MdGppGood className="me-2 mb-1 text-success" /> Link copied to
              clipboard successfully!
            </strong>
            <small>Now</small>
          </Toast.Header>
          <Toast.Body>
            <p>Invitation link is now in your clipboard ready to be shared.</p>
            <p>
              <small>{lastInvitationUrl}</small>
            </p>
          </Toast.Body>
        </Toast>

        <Toast
          onClose={() => setDisplayResendToast(false)}
          show={displayResendToast}
          delay={5000}
          autohide
          bg={resendToastVariant === "danger" ? "danger" : undefined}
        >
          <Toast.Header>
            <strong className="me-auto">
              {resendToastVariant === "success" ? (
                <MdGppGood className="me-2 mb-1 text-success" />
              ) : (
                <MdRefresh className="me-2 mb-1 text-danger" />
              )}
              {resendToastVariant === "success" ? "Invitation Resent!" : "Resend Failed"}
            </strong>
            <small>Now</small>
          </Toast.Header>
          <Toast.Body className={resendToastVariant === "danger" ? "text-white" : ""}>
            {resendToastMessage}
          </Toast.Body>
        </Toast>
      </ToastContainer>
    </>
  );
}

export default InvitationsGrid;
